import logger from '../lib/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, Phone, Mail, MessageCircle, Calendar,
  X, ChevronRight, Clock, User, Building, CheckCircle,
  TrendingUp, Users, MapPin, FileText, PhoneCall, UserCheck,
  AlertTriangle, RefreshCw, PhoneOff, ThumbsDown, Tag, UserPlus,
  ArrowUpDown, Loader2, Send, ExternalLink, Target, Star, StarOff,
  Share2, XCircle, Minus, Building2, Map, Sparkles, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { LeadActivityScheduler } from '../components/LeadActivityScheduler';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card } from '../components/ui/card';
import EntityDocuments from '../components/EntityDocuments';
import { toast } from 'sonner';

const LEAD_SOURCES = [
  { value: 'all', label: 'All Sources' },
  { value: '99acres', label: '99acres' },
  { value: 'magicbricks', label: 'MagicBricks' },
  { value: 'website', label: 'Website' },
  { value: 'manual', label: 'Manual' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const BUSINESS_VERTICALS = [
  { value: 'all', label: 'All Verticals' },
  { value: 'plots', label: 'Plots' },
  { value: 'flats', label: 'Flats' },
  { value: 'both', label: 'Both' },
  { value: 'general', label: 'General' },
];

const LEAD_STATUSES = [
  { value: 'all', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'not_picked', label: 'Not Picked' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'reassignment_pending', label: 'Reassignment Pending' },
  { value: 'site_visit_scheduled', label: 'Site Visit Scheduled' },
  { value: 'site_visit_done', label: 'Site Visit Done' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'closed', label: 'Closed' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
];

const NOT_INTERESTED_REASONS = [
  'Budget mismatch',
  'Location issue',
  'Not serious buyer',
  'Already purchased',
  'Not answering properly',
  'Other',
];

const formatLeadDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const timeStr = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (diffH < 24 && d.getDate() === now.getDate()) return `Today \u2022 ${timeStr}`;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) return `Yesterday \u2022 ${timeStr}`;
  return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} \u2022 ${timeStr}`;
};

const isNewLead = (lead) => lead.status === 'new' && !lead.last_contacted;


const PROPERTY_TYPES = [
  { value: '1BHK', label: '1 BHK' },
  { value: '2BHK', label: '2 BHK' },
  { value: '3BHK', label: '3 BHK' },
  { value: '4BHK', label: '4+ BHK' },
  { value: 'Villa', label: 'Villa' },
  { value: 'Plot', label: 'Plot' },
  { value: 'Commercial', label: 'Commercial' },
];

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, hot: 0, site_visits: 0, closed: 0, not_picked: 0, reassignment_pending: 0, follow_up_pending: 0 });
  const [leadNotes, setLeadNotes] = useState([]);
  const [leadActivities, setLeadActivities] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [previewLead, setPreviewLead] = useState(null);
  const [previewNotes, setPreviewNotes] = useState([]);
  const [previewActivity, setPreviewActivity] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [callOutcomeLead, setCallOutcomeLead] = useState(null);
  const [showCallOutcome, setShowCallOutcome] = useState(false);
  const [showPostActionNote, setShowPostActionNote] = useState(false);
  const [postActionLeadId, setPostActionLeadId] = useState(null);
  const [postActionType, setPostActionType] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [filterVertical, setFilterVertical] = useState('all');
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Not Interested modal state
  const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
  const [notInterestedLeadId, setNotInterestedLeadId] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  // Reassign modal state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignLeadId, setReassignLeadId] = useState(null);
  const [reassignAgentId, setReassignAgentId] = useState('');

  // Two-step assignment confirmation
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [pendingAssign, setPendingAssign] = useState(null); // { leadId, leadName, agentId, agentName }

  // WhatsApp section in lead detail
  const [leadWaConvos, setLeadWaConvos] = useState([]);
  const [leadWaMessages, setLeadWaMessages] = useState([]);
  const [leadWaActiveConvo, setLeadWaActiveConvo] = useState(null);
  const [leadWaInput, setLeadWaInput] = useState('');
  const [leadWaDirection, setLeadWaDirection] = useState('outbound');
  const [leadWaSending, setLeadWaSending] = useState(false);
  const [leadWaExpanded, setLeadWaExpanded] = useState(false);

  // Property matching in lead detail
  const [propMatches, setPropMatches] = useState([]);
  const [propMatchLoading, setPropMatchLoading] = useState(false);
  const [propMatchExpanded, setPropMatchExpanded] = useState(false);
  const [propReqs, setPropReqs] = useState(null);
  const [propReqEditing, setPropReqEditing] = useState(false);
  const [propReqForm, setPropReqForm] = useState({});
  const [propSavedIds, setPropSavedIds] = useState(new Set());
  const [propShareText, setPropShareText] = useState(null);

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', source: 'manual', category: 'COLD',
    budget: '', budget_max: '', property_type: '', area_preference: '',
    property_preference: '', notes: '', business_vertical: 'general',
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchLeads();
    fetchStats();
    if (isAdmin) {
      fetchAgents();
      fetchLastSyncTime();
    }
  }, [user]); // eslint-disable-line

  useEffect(() => {
    filterLeadsData();
  }, [leads, searchTerm, filterSource, filterStatus, filterAgent, filterVertical, sortBy]); // eslint-disable-line

  const fetchAgents = async () => {
    try {
      const response = await api.get('/team/agents');
      setAgents(response.data.filter(a => ['sales_rep', 'admin', 'super_admin'].includes(a.role)));
      const assignable = await api.get('/team/assignable-agents');
      setAssignableAgents(assignable.data);
    } catch (error) {
      logger.error('Failed to fetch agents');
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    }
  };

  const fetchLastSyncTime = async () => {
    try {
      const response = await api.get('/integrations/99acres/status');
      const logs = response.data?.logs || [];
      const lastSuccess = logs.find(l => l.status === 'success');
      setLastSyncTime(lastSuccess?.timestamp || null);
    } catch { /* silently ignore */ }
  };

  const [syncResult, setSyncResult] = useState(null);

  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const response = await api.post('/integrations/99acres/sync-current-month');
      const data = response.data;
      const s = data.stats || {};
      setSyncResult(data);
      if (data.success) {
        const parts = [];
        if (s.new > 0) parts.push(`${s.new} new`);
        if (s.updated > 0) parts.push(`${s.updated} updated`);
        if (s.duplicate > 0) parts.push(`${s.duplicate} unchanged`);
        if (s.error > 0) parts.push(`${s.error} failed`);
        toast.success(`Sync complete: ${parts.join(', ')} (${s.total || 0} total)`);
        fetchLeads();
        fetchStats();
        fetchLastSyncTime();
      } else {
        toast.error(data.message || 'Sync failed');
      }
    } catch (error) {
      toast.error('Unable to sync leads right now. Please try again later.');
      logger.error('99acres sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || !bulkValue || selectedLeadIds.size === 0) return;
    setBulkLoading(true);
    try {
      const response = await api.post('/leads/bulk-action', {
        lead_ids: Array.from(selectedLeadIds),
        action: bulkAction,
        value: bulkValue,
      });
      const d = response.data;
      toast.success(`${d.updated} lead${d.updated > 1 ? 's' : ''} updated`);
      setSelectedLeadIds(new Set());
      setBulkAction('');
      setBulkValue('');
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/leads/stats/summary');
      setStats(response.data);
    } catch (error) {
      logger.error('Failed to fetch stats');
    }
  };

  const filterLeadsData = useCallback(() => {
    let filtered = leads;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(l =>
        l.name?.toLowerCase().includes(term) ||
        l.phone?.includes(term) ||
        l.email?.toLowerCase().includes(term)
      );
    }
    if (filterSource !== 'all') filtered = filtered.filter(l => l.source === filterSource);
    if (filterStatus !== 'all') filtered = filtered.filter(l => l.status === filterStatus);
    if (filterVertical !== 'all') filtered = filtered.filter(l => l.business_vertical === filterVertical);
    if (filterAgent !== 'all') {
      if (filterAgent === 'unassigned') filtered = filtered.filter(l => !l.assigned_to);
      else filtered = filtered.filter(l => l.assigned_to === filterAgent);
    }
    // Sort based on user selection
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'newest':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });
    setFilteredLeads(filtered);
  }, [leads, searchTerm, filterSource, filterStatus, filterVertical, filterAgent, sortBy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', formData);
      toast.success('Lead created!');
      setIsModalOpen(false);
      resetForm();
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleStatusChange = async (leadId, status) => {
    // Intercept not_picked and not_interested
    if (status === 'not_picked') {
      try {
        const response = await api.post(`/leads/${leadId}/mark-not-picked`);
        toast.success('Marked as Not Picked - follow-up scheduled for tomorrow');
        if (response.data.suggestion) {
          toast.info(response.data.suggestion, { duration: 6000 });
        }
        fetchLeads();
        fetchStats();
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status: 'not_picked' });
          fetchLeadActivities(leadId);
        }
      } catch (error) {
        toast.error('Failed to update status');
      }
      return;
    }

    if (status === 'not_interested') {
      setNotInterestedLeadId(leadId);
      setShowNotInterestedModal(true);
      return;
    }

    try {
      await api.post(`/leads/${leadId}/status?status=${status}`);
      toast.success('Status updated');
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status });
        fetchLeadActivities(leadId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleNotInterestedSubmit = async () => {
    const reason = selectedReason === 'Other' ? otherReason : selectedReason;
    if (!reason.trim()) {
      toast.error('Please select a reason');
      return;
    }
    try {
      const response = await api.post(`/leads/${notInterestedLeadId}/mark-not-interested`, { reason });
      toast.success('Lead moved to reassignment queue');
      if (response.data.attempt_count >= 3) {
        toast.warning(`This lead has been attempted ${response.data.attempt_count} times. Consider marking it as Lost.`, { duration: 6000 });
      }
      setShowNotInterestedModal(false);
      setSelectedReason('');
      setOtherReason('');
      setNotInterestedLeadId(null);
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === notInterestedLeadId) {
        setSelectedLead({ ...selectedLead, status: 'reassignment_pending', assigned_to: null });
        fetchLeadActivities(notInterestedLeadId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAdminReassign = async () => {
    if (!reassignAgentId) {
      toast.error('Please select an agent');
      return;
    }
    try {
      const response = await api.post(`/leads/${reassignLeadId}/admin-reassign?agent_id=${reassignAgentId}`);
      toast.success(`Lead reassigned to ${response.data.assigned_to}`);
      setShowReassignModal(false);
      setReassignAgentId('');
      setReassignLeadId(null);
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === reassignLeadId) {
        setSelectedLead({ ...selectedLead, status: 'new', assigned_to: reassignAgentId });
        fetchLeadActivities(reassignLeadId);
      }
    } catch (error) {
      toast.error('Failed to reassign lead');
    }
  };

  const handleAssign = async (leadId, agentId) => {
    // Stage assignment (show confirmation dialog)
    const lead = leads.find(l => l.id === leadId);
    const agent = agents.find(a => a.id === agentId) || assignableAgents.find(a => a.id === agentId);
    const agentName = agentId === 'unassigned' ? 'Unassigned' : (agent?.name || 'Unknown');
    setPendingAssign({ leadId, leadName: lead?.name || 'Lead', agentId, agentName });
    setShowAssignConfirm(true);
  };

  const confirmAssign = async () => {
    if (!pendingAssign) return;
    try {
      const aid = pendingAssign.agentId === 'unassigned' ? '' : pendingAssign.agentId;
      await api.post(`/leads/${pendingAssign.leadId}/assign?agent_id=${aid}`);
      toast.success(`Lead successfully assigned to ${pendingAssign.agentName}`);
      setShowAssignConfirm(false);
      setPendingAssign(null);
      fetchLeads();
      if (selectedLead?.id === pendingAssign.leadId) {
        setSelectedLead({ ...selectedLead, assigned_to: pendingAssign.agentId === 'unassigned' ? null : pendingAssign.agentId });
      }
    } catch {
      toast.error('Failed to assign lead');
    }
  };

  const handleCategoryChange = async (leadId, category) => {
    try {
      await api.post(`/leads/${leadId}/categorize?category=${category}`);
      toast.success('Category updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.id}/notes?content=${encodeURIComponent(newNote)}`);
      toast.success('Note added');
      const noteContent = newNote;
      setNewNote('');
      fetchLeadNotes(selectedLead.id);
      fetchLeadActivities(selectedLead.id);
      // Update lead in list immediately with latest note
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, latest_note: noteContent, latest_note_by: user?.name || '' } : l));
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleLogCall = async () => {
    if (!selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.id}/log-call`);
      toast.success('Call logged');
      fetchLeadActivities(selectedLead.id);
    } catch (error) {
      toast.error('Failed to log call');
    }
  };

  const handleScheduleVisit = async () => {
    if (!visitDate || !selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.id}/schedule-visit?visit_date=${visitDate}`);
      toast.success('Visit scheduled!');
      setVisitDate('');
      fetchLeadActivities(selectedLead.id);
    } catch (error) {
      toast.error('Failed to schedule visit');
    }
  };

  const handleConvertToClient = async () => {
    if (!selectedLead) return;
    try {
      const response = await api.post(`/leads/${selectedLead.id}/convert-to-client`);
      toast.success(response.data.message || 'Lead converted to client!');
      setSelectedLead({ ...selectedLead, status: 'converted' });
      fetchLeads();
      fetchStats();
      fetchLeadActivities(selectedLead.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const openLeadDetail = async (lead) => {
    setSelectedLead(lead);
    setIsDetailOpen(true);
    setLeadWaConvos([]);
    setLeadWaMessages([]);
    setLeadWaActiveConvo(null);
    setLeadWaExpanded(false);
    setPropMatches([]);
    setPropMatchExpanded(false);
    setPropReqs(null);
    setPropReqEditing(false);
    setPropSavedIds(new Set());
    setMatchExplanations({});
    fetchLeadNotes(lead.id);
    fetchLeadActivities(lead.id);
    fetchLeadWhatsApp(lead.id);
    fetchLeadMatches(lead.id);
    autoExtractIfNeeded(lead.id);
  };

  const fetchLeadMatches = async (leadId) => {
    setPropMatchLoading(true);
    try {
      const [matchRes, reqRes, savedRes] = await Promise.all([
        api.get(`/leads/${leadId}/property-matches?limit=10`),
        api.get(`/leads/${leadId}/property-requirements`),
        api.get(`/leads/${leadId}/saved-matches`),
      ]);
      setPropMatches(matchRes.data);
      setPropReqs(reqRes.data);
      setPropReqForm(reqRes.data || {});
      setPropSavedIds(new Set(savedRes.data.map(s => s.property_id)));
    } catch { /* silent */ }
    setPropMatchLoading(false);
  };

  const handleSaveReqs = async () => {
    if (!selectedLead) return;
    try {
      await api.put(`/leads/${selectedLead.id}/property-requirements`, propReqForm);
      setPropReqEditing(false);
      toast.success('Requirements updated');
      fetchLeadMatches(selectedLead.id);
    } catch { toast.error('Failed to save'); }
  };

  const handleSaveMatch = async (match) => {
    if (!selectedLead || propSavedIds.has(match.property_id)) return;
    try {
      await api.post('/saved-matches', { lead_id: selectedLead.id, property_id: match.property_id, property_type: match.property_type, score: match.score });
      setPropSavedIds(prev => new Set([...prev, match.property_id]));
      toast.success('Match saved');
    } catch { toast.error('Failed to save'); }
  };

  const handlePrepareShare = async (match) => {
    if (!selectedLead) return;
    try {
      const res = await api.post('/whatsapp/share-property', {
        lead_id: selectedLead.id,
        property_id: match.property_id,
        property_type: match.property_type,
      });
      const { conversation_id, message_body } = res.data;
      setIsDetailOpen(false);
      navigate(`/whatsapp?convo=${conversation_id}&prefill=${encodeURIComponent(message_body)}`);
      toast.success('Opening WhatsApp with property details');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to prepare share');
    }
  };

  // LLM extraction + explanation
  const [extracting, setExtracting] = useState(false);
  const [matchExplanations, setMatchExplanations] = useState({});
  const [explainingId, setExplainingId] = useState(null);

  const handleExtractRequirements = async () => {
    if (!selectedLead) return;
    setExtracting(true);
    try {
      const res = await api.post(`/leads/${selectedLead.id}/extract-requirements`);
      if (res.data.success) {
        setPropReqs(res.data.requirements);
        setPropReqForm(res.data.requirements);
        toast.success('Requirements extracted from lead data');
        fetchLeadMatches(selectedLead.id);
      } else {
        toast.info(res.data.message || 'No requirements could be extracted');
      }
    } catch { toast.error('Extraction failed'); }
    setExtracting(false);
  };

  const autoExtractIfNeeded = async (leadId) => {
    try {
      const res = await api.get(`/leads/${leadId}/auto-extract-requirements`);
      if (res.data.extracted) {
        setPropReqs(res.data.requirements);
        setPropReqForm(res.data.requirements);
        toast.success('Requirements auto-extracted from lead notes');
        fetchLeadMatches(leadId);
      }
    } catch { /* silent */ }
  };

  const handleExplainMatch = async (match) => {
    if (matchExplanations[match.property_id]) return;
    setExplainingId(match.property_id);
    try {
      const res = await api.post('/property-matching/explain-match', {
        lead_id: selectedLead.id,
        property_data: { project_name: match.project_name, identifier: match.identifier, property_type: match.property_type, price_display: match.price_display, size: match.size, bhk: match.bhk, facing: match.facing },
        criteria: match.criteria,
        score: match.score,
      });
      setMatchExplanations(prev => ({ ...prev, [match.property_id]: res.data.explanation }));
    } catch { toast.error('Could not generate explanation'); }
    setExplainingId(null);
  };

  const fetchLeadWhatsApp = async (leadId) => {
    try {
      const res = await api.get(`/whatsapp/conversations?lead_id=${leadId}`);
      setLeadWaConvos(res.data);
      if (res.data.length > 0) {
        setLeadWaActiveConvo(res.data[0]);
        const msgRes = await api.get(`/whatsapp/conversations/${res.data[0].id}/messages`);
        setLeadWaMessages(msgRes.data);
      }
    } catch { /* user may not have WA access - silently handle */ }
  };

  const handleLeadWaSend = async () => {
    if (!leadWaInput.trim() || !leadWaActiveConvo) return;
    setLeadWaSending(true);
    try {
      await api.post(`/whatsapp/conversations/${leadWaActiveConvo.id}/messages`, {
        body: leadWaInput, direction: leadWaDirection, message_type: 'text', source: 'manual',
      });
      setLeadWaInput('');
      const msgRes = await api.get(`/whatsapp/conversations/${leadWaActiveConvo.id}/messages`);
      setLeadWaMessages(msgRes.data);
      toast.success('Message logged');
    } catch { toast.error('Failed to log message'); }
    setLeadWaSending(false);
  };

  const fetchLeadNotes = async (leadId) => {
    try {
      const response = await api.get(`/leads/${leadId}/notes`);
      setLeadNotes(response.data);
    } catch (error) {
      logger.error('Failed to fetch notes');
    }
  };

  const fetchLeadActivities = async (leadId) => {
    try {
      const response = await api.get(`/leads/${leadId}/activities`);
      setLeadActivities(response.data);
    } catch (error) {
      logger.error('Failed to fetch activities');
    }
  };

  const assignableFor = (bv) => assignableAgents.filter(a => !['plots', 'flats'].includes(bv) || [bv, 'all'].includes(a.business_vertical));

  const getAgentName = (agentId) => {
    if (!agentId) return 'Unassigned';
    const agent = agents.find(a => a.id === agentId) || assignableAgents.find(a => a.id === agentId);
    return agent?.name || 'Unknown';
  };

  const resetForm = () => {
    setFormData({
      name: '', phone: '', email: '', source: 'manual', category: 'COLD',
      budget: '', budget_max: '', property_type: '', area_preference: '',
      property_preference: '', notes: '', business_vertical: 'general',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getLastContacted = (lead) => {
    const lc = lead.last_contacted;
    if (!lc) return { text: 'Never', className: 'text-red-500 font-medium' };
    const now = new Date();
    const d = new Date(lc);
    const diffH = Math.floor((now - d) / (1000 * 60 * 60));
    if (diffH < 1) return { text: 'Just now', className: 'text-green-600' };
    if (diffH < 24) return { text: `${diffH}h ago`, className: 'text-green-600' };
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return { text: 'Yesterday', className: 'text-slate-600' };
    if (diffD < 7) return { text: `${diffD}d ago`, className: 'text-slate-500' };
    return { text: `${diffD}d ago`, className: 'text-red-500 font-medium' };
  };

  const handleInlineLogCall = async (lead) => {
    // Open the phone dialer - use location.href for most reliable mobile support
    window.location.href = `tel:${lead.phone}`;
    // Log call initiated
    try {
      await api.post(`/leads/${lead.id}/log-call?outcome=connected`);
    } catch (e) { logger.error('Failed to log call:', e); }
    // Show call outcome modal
    setCallOutcomeLead(lead);
    setShowCallOutcome(true);
  };

  const handleCallOutcome = async (outcome) => {
    if (!callOutcomeLead) return;
    try {
      await api.post(`/leads/${callOutcomeLead.id}/log-call?outcome=${outcome}`);
      if (outcome === 'not_picked') toast.info('Follow-up scheduled for tomorrow');
      else if (outcome === 'call_later') toast.info('Reminder set for 2 hours');
      else toast.success('Call logged as connected');
    } catch { toast.error('Failed to log outcome'); }
    setShowCallOutcome(false);
    // Show post-action note popup
    setPostActionLeadId(callOutcomeLead.id);
    setPostActionType('call');
    setShowPostActionNote(true);
    setCallOutcomeLead(null);
    fetchLeads();
    fetchStats();
  };

  const getWhatsAppMessage = (lead) => {
    const name = lead.name || '';
    const source = lead.source || 'our portal';
    const propType = lead.property_type || 'property';
    const area = lead.area_preference || '';
    
    if (lead.status === 'not_picked') {
      return `Hi ${name}, I tried calling you regarding your property inquiry. Please let me know a convenient time to connect. - Aavaas Consultants`;
    }
    if (['contacted', 'site_visit_scheduled', 'negotiation'].includes(lead.status)) {
      return `Hi ${name}, just following up regarding your interest in ${propType}${area ? ' in ' + area : ''}. Let me know when you're available to discuss. - Aavaas Consultants`;
    }
    // New lead or default
    return `Hi ${name}, I'm reaching out from Aavaas Consultants regarding your property inquiry on ${source}. Please let me know a good time to connect.`;
  };

  const handleWhatsApp = async (lead) => {
    const phone = lead.phone?.replace(/\D/g, '');
    const msg = encodeURIComponent(getWhatsAppMessage(lead));
    // Desktop → WhatsApp Web, Mobile → wa.me
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const url = isMobile ? `https://wa.me/${phone}?text=${msg}` : `https://web.whatsapp.com/send?phone=${phone}&text=${msg}`;
    // Use anchor click for reliable mobile support (window.open gets blocked)
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Log WhatsApp activity
    try {
      await api.post(`/leads/${lead.id}/log-whatsapp`);
      toast.success('WhatsApp logged');
    } catch (e) { logger.error('Failed to log WhatsApp:', e); }
    // Show post-action note popup
    setPostActionLeadId(lead.id);
    setPostActionType('whatsapp');
    setShowPostActionNote(true);
    fetchLeads();
  };

  const handlePostActionNote = async (note) => {
    if (!note?.trim() || !postActionLeadId) { setShowPostActionNote(false); return; }
    try {
      await api.post(`/leads/${postActionLeadId}/notes?content=${encodeURIComponent(note)}`);
      toast.success('Note saved');
      setLeads(prev => prev.map(l => l.id === postActionLeadId ? { ...l, latest_note: note, latest_note_by: user?.name || '' } : l));
    } catch { toast.error('Failed to save note'); }
    setShowPostActionNote(false);
    setPostActionLeadId(null);
  };

  const handleInlineAddNote = async (leadId, note) => {
    if (!note?.trim()) return;
    try {
      await api.post(`/leads/${leadId}/notes?content=${encodeURIComponent(note)}`);
      toast.success('Note added!');
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, latest_note: note, latest_note_by: user?.name || '' } : l));
    } catch { toast.error('Failed to add note'); }
  };

  const getRowHighlight = (lead) => {
    const now = new Date();
    const created = new Date(lead.created_at);
    const isToday = (now - created) < 24 * 60 * 60 * 1000;
    if (lead.status === 'not_picked') return 'border-l-4 border-l-orange-400 bg-orange-50/30';
    if (lead.status === 'reassignment_pending') return 'border-l-4 border-l-purple-400 bg-purple-50/30';
    if (isToday && lead.status === 'new') return 'border-l-4 border-l-blue-400 bg-blue-50/30';
    // Stale: no activity > 24h and still active
    const updated = new Date(lead.updated_at || lead.created_at);
    if ((now - updated) > 24 * 60 * 60 * 1000 && ['new', 'contacted'].includes(lead.status)) return 'border-l-4 border-l-red-400';
    return '';
  };

  const unassignedCount = useMemo(() => leads.filter(l => !l.assigned_to && !['closed', 'converted', 'lost'].includes(l.status)).length, [leads]);

  const fetchPreview = async (lead) => {
    if (previewLead?.id === lead.id) return;
    setPreviewLead(lead);
    setPreviewLoading(true);
    try {
      const [notesRes, actRes] = await Promise.all([
        api.get(`/leads/${lead.id}/notes`),
        api.get(`/leads/${lead.id}/activities`),
      ]);
      setPreviewNotes(notesRes.data.slice(0, 3));
      setPreviewActivity(actRes.data[0] || null);
    } catch { setPreviewNotes([]); setPreviewActivity(null); }
    setPreviewLoading(false);
  };

  const formatBudget = (budget, budgetMax) => {
    if (!budget && budget !== 0) return '-';
    const formatNum = (n) => {
      const num = typeof n === 'string' ? parseFloat(n) : n;
      if (isNaN(num)) return n;
      if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
      if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
      return num.toString();
    };
    if (typeof budget === 'string' && /[A-Za-z]/.test(budget)) return `₹${budget}`;
    if (budgetMax) return `₹${formatNum(budget)} - ${formatNum(budgetMax)}`;
    return `₹${formatNum(budget)}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      contacted: 'bg-amber-50 text-amber-700 border-amber-200',
      not_picked: 'bg-orange-50 text-orange-700 border-orange-200',
      not_interested: 'bg-red-50 text-red-700 border-red-200',
      reassignment_pending: 'bg-purple-50 text-purple-700 border-purple-200',
      site_visit_scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      site_visit_done: 'bg-violet-50 text-violet-700 border-violet-200',
      site_visit: 'bg-purple-50 text-purple-700 border-purple-200',
      negotiation: 'bg-orange-50 text-orange-700 border-orange-200',
      closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      converted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      lost: 'bg-red-50 text-red-700 border-red-200',
    };
    const labels = {
      new: 'New', contacted: 'Contacted', not_picked: 'Not Picked',
      not_interested: 'Not Interested', reassignment_pending: 'Reassignment Pending',
      site_visit_scheduled: 'Visit Scheduled', site_visit_done: 'Visit Done',
      site_visit: 'Site Visit', negotiation: 'Negotiation',
      closed: 'Closed', converted: 'Converted', lost: 'Lost',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${styles[status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const styles = { HOT: 'bg-rose-500 text-white shadow-sm', WARM: 'bg-amber-400 text-white', COLD: 'bg-slate-400 text-white' };
    return <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide ${styles[category] || 'bg-slate-200'}`}>{category}</span>;
  };

  const getSourceBadge = (source) => {
    const styles = {
      '99acres': 'bg-indigo-50 text-indigo-700 border-indigo-200', 'magicbricks': 'bg-amber-50 text-amber-700 border-amber-200',
      'website': 'bg-teal-50 text-teal-700 border-teal-200', 'manual': 'bg-slate-50 text-slate-600 border-slate-200',
      'facebook': 'bg-blue-50 text-blue-700 border-blue-200', 'whatsapp': 'bg-green-50 text-green-700 border-green-200',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[source] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{source}</span>;
  };

  const getVerticalBadge = (vertical) => {
    const styles = {
      plots: 'bg-amber-50 text-amber-700 border-amber-200',
      flats: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      both: 'bg-violet-50 text-violet-700 border-violet-200',
      general: 'bg-slate-50 text-slate-500 border-slate-200',
    };
    const labels = { plots: 'Plot', flats: 'Flat', both: 'Both', general: 'General' };
    if (!vertical || vertical === 'general') return null;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[vertical] || styles.general}`}>{labels[vertical] || vertical}</span>;
  };

  return (
    <Layout>
      <div className="aavaas-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="aavaas-page-title" data-testid="leads-title">Lead Management</h1>
            <p className="aavaas-page-subtitle">Track and manage all your leads</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <div className="flex items-center gap-2">
                <div className="text-right hidden sm:block">
                  <p className="text-[10px] text-slate-400 leading-tight">Last sync</p>
                  <p className="text-[11px] text-slate-500 font-medium" data-testid="last-sync-time">
                    {lastSyncTime ? formatLeadDate(lastSyncTime) : 'Never'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="h-9 text-sm gap-1.5 border-slate-200"
                  onClick={handleSync}
                  disabled={isSyncing}
                  data-testid="sync-leads-button"
                >
                  {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {isSyncing ? 'Syncing...' : 'Sync Leads'}
                </Button>
              </div>
            )}
            {/* Sync Result Banner */}
            {syncResult && !isSyncing && (
              <div className={`text-xs px-2.5 py-1.5 rounded-lg border ${syncResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="sync-result">
                <p className="font-medium">{syncResult.success ? 'Sync completed' : 'Sync failed'}</p>
                {syncResult.stats && (
                  <p className="mt-0.5">
                    {syncResult.stats.new > 0 && <span className="text-emerald-600 font-semibold">{syncResult.stats.new} new </span>}
                    {syncResult.stats.updated > 0 && <span className="text-blue-600 font-semibold">{syncResult.stats.updated} updated </span>}
                    {syncResult.stats.duplicate > 0 && <span>{syncResult.stats.duplicate} unchanged </span>}
                    {syncResult.stats.error > 0 && <span className="text-red-600 font-semibold">{syncResult.stats.error} failed </span>}
                    {syncResult.stats.chunks_failed > 0 && <span className="text-amber-600">({syncResult.stats.chunks_failed} chunk errors)</span>}
                  </p>
                )}
                {!syncResult.success && <p className="mt-0.5 text-[10px]">{syncResult.message}</p>}
                <button className="text-[10px] underline mt-0.5 opacity-70" onClick={() => setSyncResult(null)}>dismiss</button>
              </div>
            )}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogTrigger asChild>
                <Button className="aavaas-btn-primary" data-testid="add-lead-button">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Lead
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add New Lead</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="lead-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="lead-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="lead-email-input" />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="99acres">99acres</SelectItem>
                        <SelectItem value="magicbricks">MagicBricks</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Requirement Type *</Label>
                    <Select value={formData.business_vertical} onValueChange={(v) => setFormData({ ...formData, business_vertical: v })} data-testid="lead-vertical-select">
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="plots">Plot</SelectItem>
                        <SelectItem value="flats">Flat</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                        <SelectItem value="general">General / Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <Select value={formData.property_type || 'none'} onValueChange={(v) => setFormData({ ...formData, property_type: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Area / Location</Label>
                    <Input value={formData.area_preference} onChange={(e) => setFormData({ ...formData, area_preference: e.target.value })} placeholder="e.g., Mohali, Chandigarh" />
                  </div>
                  <div>
                    <Label>Budget Min (₹)</Label>
                    <Input value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="e.g., 50L" />
                  </div>
                  <div>
                    <Label>Budget Max (₹)</Label>
                    <Input value={formData.budget_max} onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })} placeholder="e.g., 1.2Cr" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HOT">HOT</SelectItem>
                        <SelectItem value="WARM">WARM</SelectItem>
                        <SelectItem value="COLD">COLD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Additional Requirements</Label>
                    <Input value={formData.property_preference} onChange={(e) => setFormData({ ...formData, property_preference: e.target.value })} placeholder="e.g., Near metro, Park facing" />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." rows={3} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-lead-button">Add Lead</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4 mb-6 stagger">
          {[
            { key: 'total', icon: Users, value: stats.total, label: 'Total', color: 'text-slate-900', bg: 'bg-slate-50', testId: 'stat-total' },
            { key: 'new', icon: TrendingUp, value: stats.new, label: 'New', color: 'text-blue-600', bg: 'bg-blue-50', testId: 'stat-new' },
            { key: 'hot', icon: TrendingUp, value: stats.hot, label: 'Hot', color: 'text-rose-600', bg: 'bg-rose-50', testId: 'stat-hot' },
            { key: 'not_picked', icon: PhoneOff, value: stats.not_picked, label: 'Not Picked', color: 'text-orange-600', bg: 'bg-orange-50', testId: 'stat-not-picked' },
            ...(isAdmin ? [{ key: 'reassign', icon: RefreshCw, value: stats.reassignment_pending, label: 'Reassign', color: 'text-purple-600', bg: 'bg-purple-50', testId: 'stat-reassignment', onClick: () => setFilterStatus('reassignment_pending') }] : []),
            { key: 'closed', icon: Building, value: stats.closed, label: 'Closed', color: 'text-emerald-600', bg: 'bg-emerald-50', testId: 'stat-closed' },
          ].map(s => (
            <div key={s.key} className={`animate-slide-up bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-3 card-hover ${s.onClick ? 'cursor-pointer' : ''}`} data-testid={s.testId} onClick={s.onClick}>
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 ${s.bg} rounded-lg`}><s.icon className={`w-4 h-4 ${s.color}`} /></div>
                <div>
                  <p className={`text-xl font-bold ${s.color} tracking-tight`}>{s.value}</p>
                  <p className="text-[10px] text-slate-500 font-medium">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Unassigned Alert */}
        {isAdmin && unassignedCount > 0 && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl" data-testid="unassigned-alert">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <span className="text-sm text-orange-800 font-medium">{unassignedCount} lead{unassignedCount > 1 ? 's are' : ' is'} unassigned</span>
            <Button size="sm" variant="outline" className="ml-auto h-7 text-xs border-orange-300 text-orange-700" onClick={() => setFilterAgent('unassigned')}>
              Show Unassigned
            </Button>
          </div>
        )}

        {/* Filters & Search */}
        <div className="aavaas-toolbar mb-6">
            <div className="relative flex-1 min-w-[140px] lg:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by name, phone, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="aavaas-search" data-testid="search-leads-input" />
            </div>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-[140px] bg-white border-slate-200 h-9 text-sm rounded-lg shadow-sm" data-testid="filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_SOURCES.map(src => <SelectItem key={src.value} value={src.value}>{src.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[170px] bg-white border-slate-200 h-9 text-sm rounded-lg shadow-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger className="w-[150px] bg-white border-slate-200 h-9 text-sm rounded-lg shadow-sm"><SelectValue placeholder="All Agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px] bg-white border-slate-200 h-9 text-sm rounded-lg shadow-sm" data-testid="leads-sort-select">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-slate-400 flex-shrink-0" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="name_asc">Name A–Z</SelectItem>
                <SelectItem value="name_desc">Name Z–A</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterVertical} onValueChange={setFilterVertical}>
              <SelectTrigger className="w-[140px] bg-white border-slate-200 h-9 text-sm rounded-lg shadow-sm" data-testid="filter-vertical">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_VERTICALS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {(filterSource !== 'all' || filterStatus !== 'all' || filterAgent !== 'all' || filterVertical !== 'all' || searchTerm) && (
              <Button variant="ghost" size="sm" className="h-9 text-xs font-medium" onClick={() => { setFilterSource('all'); setFilterStatus('all'); setFilterAgent('all'); setFilterVertical('all'); setSearchTerm(''); setSelectedLeadIds(new Set()); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

        {/* Bulk Action Bar */}
        {isAdmin && selectedLeadIds.size > 0 && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-slate-900 text-white rounded-xl flex-wrap" data-testid="bulk-action-bar">
            <span className="text-sm font-semibold">{selectedLeadIds.size} selected</span>
            <Select value={bulkAction} onValueChange={(v) => { setBulkAction(v); setBulkValue(''); }}>
              <SelectTrigger className="w-[130px] h-8 text-xs bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Action..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assign">Assign</SelectItem>
                <SelectItem value="classify">Classify</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
            {bulkAction === 'assign' && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger className="w-[160px] h-8 text-xs bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select agent..." /></SelectTrigger>
                <SelectContent>
                  {assignableFor(null).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'classify' && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select vertical..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="plots">Plot</SelectItem>
                  <SelectItem value="flats">Flat</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'status' && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger className="w-[160px] h-8 text-xs bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select status..." /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.filter(s => s.value !== 'all').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" className="h-8 bg-white text-slate-900 hover:bg-slate-100 text-xs font-semibold" onClick={handleBulkAction} disabled={!bulkValue || bulkLoading} data-testid="bulk-apply-btn">
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Apply
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => { setSelectedLeadIds(new Set()); setBulkAction(''); setBulkValue(''); }}>
              Cancel
            </Button>
          </div>
        )}

        {/* Mobile Lead Cards */}
        <div className="lg:hidden space-y-2 mb-4">
          {filteredLeads.length === 0 ? (
            <Card className="p-8 text-center"><p className="text-slate-500">No leads found</p></Card>
          ) : filteredLeads.map(lead => {
            const lc = getLastContacted(lead);
            return (
              <div key={lead.id} className={`bg-white rounded-xl shadow-sm p-3 border ${getRowHighlight(lead) || 'border-slate-100'}`}
                onClick={() => openLeadDetail(lead)} data-testid={`mobile-lead-${lead.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0 flex items-start gap-2">
                    {isAdmin && (
                      <input type="checkbox" className="mt-1 w-4 h-4 rounded border-slate-300 accent-slate-900 flex-shrink-0" checked={selectedLeadIds.has(lead.id)} onChange={(e) => { e.stopPropagation(); toggleLeadSelection(lead.id); }} onClick={(e) => e.stopPropagation()} />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {getCategoryBadge(lead.category)}
                        <span className="font-semibold text-sm text-slate-900 truncate">{lead.name}</span>
                        {isNewLead(lead) && <span className="px-1.5 py-0.5 rounded bg-blue-500 text-white text-[9px] font-bold tracking-wide leading-none" data-testid={`new-badge-${lead.id}`}>NEW</span>}
                        {getVerticalBadge(lead.business_vertical)}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{lead.phone}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(lead.status)}
                    {getSourceBadge(lead.source)}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2 flex-wrap">
                  {lead.property_type && <span>{lead.property_type}</span>}
                  {lead.budget && <span className="font-medium text-slate-700">{formatBudget(lead.budget, lead.budget_max)}</span>}
                  <span className={lc.className}>{lc.text}</span>
                  <span className="text-slate-400 ml-auto" data-testid={`mobile-lead-date-${lead.id}`} title={lead.enquiry_date || lead.created_at}>{formatLeadDate(lead.enquiry_date || lead.created_at)}</span>
                </div>
                {lead.latest_note && (
                  <p className="text-[11px] text-slate-400 truncate mb-2 pl-6" title={lead.latest_note}>📝 {lead.latest_note}</p>
                )}
                <div className="flex items-center gap-1 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-9 flex-1 text-xs gap-1" onClick={() => handleInlineLogCall(lead)}>
                    <Phone className="w-4 h-4 text-green-600" /> Call
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 flex-1 text-xs gap-1" onClick={() => handleWhatsApp(lead)}>
                    <MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 flex-1 text-xs gap-1" onClick={() => { const n = window.prompt('Quick note:'); if (n) handleInlineAddNote(lead.id, n); }}>
                    <FileText className="w-4 h-4 text-slate-400" /> Note
                  </Button>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 px-1" onClick={(e) => e.stopPropagation()}>
                    <UserPlus className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <Select value={lead.assigned_to || 'unassigned'} onValueChange={(value) => handleAssign(lead.id, value)}>
                      <SelectTrigger className="h-8 text-xs flex-1 border-slate-200" data-testid={`mobile-assign-${lead.id}`}>
                        <SelectValue>{getAgentName(lead.assigned_to)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {assignableFor(lead.business_vertical).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop Leads Table */}
        <div className="hidden lg:block bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead className="bg-slate-50/80 border-b border-slate-200">
                <tr>
                  {isAdmin && <th className="px-3 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded border-slate-300 accent-slate-900" checked={filteredLeads.length > 0 && selectedLeadIds.size === filteredLeads.length} onChange={toggleSelectAll} data-testid="select-all-checkbox" /></th>}
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Lead</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Received</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Requirement</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Budget</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Last Contact</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Assigned</th>}
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 11 : 9} className="px-4 py-16 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                      <p className="font-semibold">No leads found</p>
                      <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or add a new lead</p>
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr key={lead.id} className={`table-row-hover cursor-pointer ${getRowHighlight(lead)}`} onClick={() => openLeadDetail(lead)} data-testid={`lead-row-${lead.id}`}>
                      {isAdmin && <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded border-slate-300 accent-slate-900" checked={selectedLeadIds.has(lead.id)} onChange={() => toggleLeadSelection(lead.id)} /></td>}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {getCategoryBadge(lead.category)}
                          <Popover>
                            <PopoverTrigger asChild>
                              <div className="cursor-pointer" onMouseEnter={() => fetchPreview(lead)} onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-slate-900 hover:text-blue-700 hover:underline transition-colors">{lead.name}</span>
                                  {isNewLead(lead) && <span className="px-1.5 py-0.5 rounded bg-blue-500 text-white text-[9px] font-bold tracking-wide leading-none" data-testid={`new-badge-${lead.id}`}>NEW</span>}
                                  {getVerticalBadge(lead.business_vertical)}
                                </div>
                                {lead.latest_note && <p className="text-[10px] text-slate-400 truncate max-w-[180px] mt-0.5">📝 {lead.latest_note}</p>}
                                {lead.tags && lead.tags.length > 0 && (
                                  <div className="flex gap-1 mt-0.5">
                                    {lead.tags.map(tag => (
                                      <span key={tag} className={`text-[10px] px-1.5 py-0.5 rounded ${tag === 'Follow-up Pending' ? 'bg-orange-100 text-orange-600' : tag === 'Needs Reassignment' ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-500'}`}>{tag}</span>
                                    ))}
                                  </div>
                                )}
                                {(lead.attempt_count || 0) > 0 && (
                                  <span className="text-[10px] text-slate-400 ml-1">Attempts: {lead.attempt_count}</span>
                                )}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-0" side="right" align="start" sideOffset={8}>
                              <div className="p-3 border-b bg-slate-50 rounded-t-md">
                                <div className="flex items-center justify-between">
                                  <p className="font-semibold text-sm text-slate-900">{lead.name}</p>
                                  {getCategoryBadge(lead.category)}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">{lead.phone} {lead.email ? `| ${lead.email}` : ''}</p>
                              </div>
                              <div className="p-3 space-y-2.5 text-sm">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-[10px] text-slate-400 uppercase">Requirement</p>
                                    <p className="text-slate-800 font-medium">{lead.property_type || '-'}</p>
                                    {lead.area_preference && <p className="text-xs text-slate-500">{lead.area_preference}</p>}
                                    {lead.property_preference && <p className="text-xs text-slate-500 truncate">{lead.property_preference}</p>}
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-slate-400 uppercase">Budget</p>
                                    <p className="text-slate-800 font-medium">{formatBudget(lead.budget, lead.budget_max)}</p>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Last Activity</p>
                                  {previewLoading ? <p className="text-xs text-slate-400">Loading...</p> : previewActivity ? (
                                    <p className="text-xs text-slate-600">{previewActivity.description} <span className="text-slate-400">- {formatDate(previewActivity.created_at)}</span></p>
                                  ) : <p className="text-xs text-slate-400">No activity yet</p>}
                                </div>
                                {!previewLoading && previewNotes.length > 0 && (
                                  <div>
                                    <p className="text-[10px] text-slate-400 uppercase">Recent Notes</p>
                                    {previewNotes.map((n) => (
                                      <p key={n.id || n.content?.slice(0, 20)} className="text-xs text-slate-600 truncate">{n.content}</p>
                                    ))}
                                  </div>
                                )}
                                <div className="flex gap-1 pt-1 border-t">
                                  <Button size="sm" variant="ghost" className="h-7 text-xs flex-1 hover:bg-green-50" onClick={() => handleInlineLogCall(lead)}>
                                    <Phone className="w-3 h-3 mr-1 text-green-600" /> Call
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs flex-1 hover:bg-green-50" onClick={() => handleWhatsApp(lead)}>
                                    <MessageCircle className="w-3 h-3 mr-1 text-green-500" /> WhatsApp
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => openLeadDetail(lead)}>
                                    Full Details
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-slate-600">{lead.phone}</div>
                        {lead.email && <div className="text-xs text-slate-400">{lead.email}</div>}
                      </td>
                      <td className="px-3 py-3" data-testid={`lead-date-${lead.id}`} title={lead.enquiry_date || lead.created_at}>
                        <div className="text-xs text-slate-500 whitespace-nowrap">{formatLeadDate(lead.enquiry_date || lead.created_at)}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-slate-900">{lead.property_type || '-'}</div>
                        {lead.area_preference && <div className="text-xs text-slate-500">{lead.area_preference}</div>}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-900">{formatBudget(lead.budget, lead.budget_max)}</td>
                      <td className="px-3 py-3">{getSourceBadge(lead.source)}</td>
                      <td className="px-3 py-3">
                        {(() => { const lc = getLastContacted(lead); return <span className={`text-xs ${lc.className}`}>{lc.text}</span>; })()}
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        {lead.status === 'reassignment_pending' && isAdmin ? (
                          <div className="flex items-center gap-1">
                            {getStatusBadge(lead.status)}
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-purple-600" onClick={() => { setReassignLeadId(lead.id); setShowReassignModal(true); }} data-testid={`reassign-btn-${lead.id}`}>
                              Assign
                            </Button>
                          </div>
                        ) : (
                          <Select value={lead.status} onValueChange={(value) => handleStatusChange(lead.id, value)}>
                            <SelectTrigger className="w-[140px] h-8 text-xs border-0 bg-transparent p-0">
                              {getStatusBadge(lead.status)}
                            </SelectTrigger>
                            <SelectContent>
                              {LEAD_STATUSES.filter(s => s.value !== 'all').map(st => (
                                <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-3 text-sm text-slate-600" onClick={(e) => e.stopPropagation()}>
                          <Select value={lead.assigned_to || 'unassigned'} onValueChange={(value) => handleAssign(lead.id, value)}>
                            <SelectTrigger className="w-[120px] h-8 text-xs">
                              <SelectValue>{getAgentName(lead.assigned_to)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {assignableFor(lead.business_vertical).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-0.5">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" title="Call" onClick={() => handleInlineLogCall(lead)} data-testid={`call-${lead.id}`}>
                            <Phone className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" title="WhatsApp" onClick={() => handleWhatsApp(lead)} data-testid={`whatsapp-${lead.id}`}>
                            <MessageCircle className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Add Quick Note" onClick={() => {
                            const note = window.prompt('Quick note:');
                            if (note) handleInlineAddNote(lead.id, note);
                          }} data-testid={`note-${lead.id}`}>
                            <FileText className="w-4 h-4 text-slate-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openLeadDetail(lead)} title="Details">
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead Detail Panel */}
        <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            {selectedLead && (
              <>
                <SheetHeader className="mb-6">
                  <div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setIsDetailOpen(false)} className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="lead-detail-back">
                        <ArrowLeft className="w-5 h-5 text-slate-500" />
                      </button>
                      <SheetTitle className="text-xl">{selectedLead.name}</SheetTitle>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {getCategoryBadge(selectedLead.category)}
                      {getStatusBadge(selectedLead.status)}
                      {getSourceBadge(selectedLead.source)}
                      {selectedLead.tags?.map(tag => (
                        <span key={tag} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tag === 'Follow-up Pending' ? 'bg-orange-100 text-orange-600' : tag === 'Needs Reassignment' ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-500'}`}>{tag}</span>
                      ))}
                    </div>
                    {(selectedLead.attempt_count || 0) > 0 && (
                      <div className={`mt-2 flex items-center gap-1 text-xs ${selectedLead.attempt_count >= 3 ? 'text-red-600' : 'text-slate-500'}`}>
                        <RefreshCw className="w-3 h-3" /> Attempts: {selectedLead.attempt_count}
                        {selectedLead.attempt_count >= 3 && <AlertTriangle className="w-3 h-3 ml-1" />}
                      </div>
                    )}
                  </div>
                </SheetHeader>

                <div className="space-y-6">
                  {/* Reassignment Warning */}
                  {selectedLead.attempt_count >= 3 && isAdmin && (
                    <Card className="p-3 bg-amber-50 border-amber-200">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Multiple attempts ({selectedLead.attempt_count})</p>
                          <p className="text-amber-700 text-xs mt-1">This lead has been attempted multiple times. Consider marking it as Lost.</p>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setReassignLeadId(selectedLead.id); setShowReassignModal(true); }}>Reassign Again</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleStatusChange(selectedLead.id, 'lost')}>Mark as Lost</Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Reassignment Info */}
                  {selectedLead.status === 'reassignment_pending' && (
                    <Card className="p-3 bg-purple-50 border-purple-200">
                      <div className="text-sm">
                        <p className="font-medium text-purple-800">Pending Reassignment</p>
                        {selectedLead.previous_agent_name && <p className="text-xs text-purple-600 mt-1">Previous agent: {selectedLead.previous_agent_name}</p>}
                        {selectedLead.rejection_reason && <p className="text-xs text-purple-600">Reason: {selectedLead.rejection_reason}</p>}
                        {isAdmin && (
                          <Button size="sm" className="mt-2 h-7 text-xs bg-purple-600 hover:bg-purple-700" onClick={() => { setReassignLeadId(selectedLead.id); setShowReassignModal(true); }} data-testid="detail-reassign-btn">
                            Assign Agent
                          </Button>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Assign Agent (admin only, all leads) */}
                  {isAdmin && (
                    <Card className="p-4">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-slate-400" /> Assign Agent
                      </h3>
                      <Select value={selectedLead.assigned_to || 'unassigned'} onValueChange={(value) => { handleAssign(selectedLead.id, value); setSelectedLead({ ...selectedLead, assigned_to: value === 'unassigned' ? null : value }); }}>
                        <SelectTrigger className="h-10 text-sm" data-testid="detail-assign-select">
                          <SelectValue>{getAgentName(selectedLead.assigned_to)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {assignableFor(selectedLead.business_vertical).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Card>
                  )}

                  {/* Contact Info */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Contact Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a href={`tel:${selectedLead.phone}`} className="text-blue-600 hover:underline">{selectedLead.phone}</a>
                      </div>
                      {selectedLead.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">{selectedLead.email}</a>
                        </div>
                      )}
                    </div>
                  </Card>

                  <LeadActivityScheduler lead={selectedLead} agentName={getAgentName(selectedLead.assigned_to)} />

                  {/* Property Requirements (Structured) */}
                  <Card className="p-4" data-testid="lead-property-requirements">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-500" /> Property Requirement
                      </h3>
                      <div className="flex items-center gap-2">
                        <button className="text-xs text-purple-600 hover:underline flex items-center gap-1" onClick={handleExtractRequirements} disabled={extracting} data-testid="extract-requirements-btn">
                          {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {extracting ? 'Extracting...' : 'AI Extract'}
                        </button>
                        <button className="text-xs text-blue-600 hover:underline" onClick={() => { setPropReqEditing(!propReqEditing); setPropReqForm(propReqs || {}); }} data-testid="edit-requirements-btn">
                          {propReqEditing ? 'Cancel' : 'Edit'}
                        </button>
                      </div>
                    </div>
                    {propReqEditing ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Requirement Type</label>
                            <Select value={propReqForm.requirement_type || 'general'} onValueChange={v => setPropReqForm(p => ({...p, requirement_type: v}))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="flat">Flat</SelectItem>
                                <SelectItem value="plot">Plot</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">BHK (Flats)</label>
                            <Select value={propReqForm.bhk || '_none'} onValueChange={v => setPropReqForm(p => ({...p, bhk: v === '_none' ? null : v}))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">Any</SelectItem>
                                <SelectItem value="1 BHK">1 BHK</SelectItem>
                                <SelectItem value="2 BHK">2 BHK</SelectItem>
                                <SelectItem value="3 BHK">3 BHK</SelectItem>
                                <SelectItem value="4 BHK">4 BHK</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Target Budget</label>
                            <Input className="h-8 text-xs" type="number" placeholder="e.g. 15000000" value={propReqForm.target_budget || ''} onChange={e => setPropReqForm(p => ({...p, target_budget: e.target.value ? Number(e.target.value) : null}))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Max Budget</label>
                            <Input className="h-8 text-xs" type="number" placeholder="e.g. 16500000" value={propReqForm.max_budget || ''} onChange={e => setPropReqForm(p => ({...p, max_budget: e.target.value ? Number(e.target.value) : null}))} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Preferred Project</label>
                            <Input className="h-8 text-xs" placeholder="e.g. Aerocity" value={propReqForm.preferred_project || ''} onChange={e => setPropReqForm(p => ({...p, preferred_project: e.target.value || null}))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Preferred Location</label>
                            <Input className="h-8 text-xs" placeholder="e.g. Mohali" value={propReqForm.preferred_location || ''} onChange={e => setPropReqForm(p => ({...p, preferred_location: e.target.value || null}))} />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Pref. Size</label>
                            <Input className="h-8 text-xs" type="number" placeholder="e.g. 500" value={propReqForm.preferred_size || ''} onChange={e => setPropReqForm(p => ({...p, preferred_size: e.target.value ? Number(e.target.value) : null}))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Min Size</label>
                            <Input className="h-8 text-xs" type="number" value={propReqForm.min_size || ''} onChange={e => setPropReqForm(p => ({...p, min_size: e.target.value ? Number(e.target.value) : null}))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Max Size</label>
                            <Input className="h-8 text-xs" type="number" value={propReqForm.max_size || ''} onChange={e => setPropReqForm(p => ({...p, max_size: e.target.value ? Number(e.target.value) : null}))} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Facing</label>
                            <Select value={propReqForm.facing || '_none'} onValueChange={v => setPropReqForm(p => ({...p, facing: v === '_none' ? null : v}))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">Any</SelectItem>
                                <SelectItem value="East">East</SelectItem>
                                <SelectItem value="West">West</SelectItem>
                                <SelectItem value="North">North</SelectItem>
                                <SelectItem value="South">South</SelectItem>
                                <SelectItem value="North-East">North-East</SelectItem>
                                <SelectItem value="Park Facing">Park Facing</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 font-medium">Furnishing</label>
                            <Select value={propReqForm.furnishing || '_none'} onValueChange={v => setPropReqForm(p => ({...p, furnishing: v === '_none' ? null : v}))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">Any</SelectItem>
                                <SelectItem value="furnished">Furnished</SelectItem>
                                <SelectItem value="semi-furnished">Semi-furnished</SelectItem>
                                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button size="sm" className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700" onClick={handleSaveReqs} data-testid="save-requirements-btn">Save Requirements & Refresh Matches</Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Type:</span><p className="font-medium capitalize">{propReqs?.requirement_type || selectedLead.business_vertical || '—'}</p></div>
                        <div><span className="text-slate-500">Budget:</span><p className="font-medium">{formatBudget(selectedLead.budget, selectedLead.budget_max)}</p></div>
                        {propReqs?.bhk && <div><span className="text-slate-500">BHK:</span><p className="font-medium">{propReqs.bhk}</p></div>}
                        {propReqs?.preferred_project && <div><span className="text-slate-500">Project:</span><p className="font-medium">{propReqs.preferred_project}</p></div>}
                        {propReqs?.preferred_location && <div><span className="text-slate-500">Location:</span><p className="font-medium">{propReqs.preferred_location}</p></div>}
                        {propReqs?.preferred_size && <div><span className="text-slate-500">Size:</span><p className="font-medium">{propReqs.preferred_size} {propReqs.size_unit || 'sqft'}</p></div>}
                        {propReqs?.facing && <div><span className="text-slate-500">Facing:</span><p className="font-medium">{propReqs.facing}</p></div>}
                        <div><span className="text-slate-500">Added:</span><p className="font-medium">{formatDate(selectedLead.created_at)}</p></div>
                        {selectedLead.property_preference && (
                          <div className="col-span-2 pt-2 border-t"><span className="text-slate-500 text-xs">Additional:</span><p className="text-xs text-slate-600 mt-0.5">{selectedLead.property_preference}</p></div>
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Matching Properties */}
                  <Card className="p-4" data-testid="lead-matching-properties">
                    <button className="w-full flex items-center justify-between" onClick={() => setPropMatchExpanded(!propMatchExpanded)}>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Target className="w-4 h-4 text-emerald-600" /> Matching Properties
                        {propMatches.length > 0 && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{propMatches.length}</span>}
                        {propMatchLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                      </h3>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${propMatchExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    {propMatchExpanded && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-400">{propMatches.length} matches found</span>
                          <button className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5" onClick={() => fetchLeadMatches(selectedLead.id)} data-testid="refresh-lead-matches">
                            <RefreshCw className="w-3 h-3" /> Refresh
                          </button>
                        </div>
                        {propMatches.length === 0 && !propMatchLoading && (
                          <p className="text-xs text-slate-400 text-center py-3">No matches. Try editing requirements above.</p>
                        )}
                        {propMatches.slice(0, 8).map(m => (
                          <div key={m.property_id} className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`lead-match-${m.property_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  {m.property_type === 'flat' ? <Building2 className="w-3 h-3 text-blue-500" /> : <Map className="w-3 h-3 text-green-600" />}
                                  <span className="text-xs font-semibold text-slate-900 truncate">{m.project_name}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-0.5">{m.identifier} · {m.price_display} · {m.size !== '—' ? m.size : ''}</p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className={`text-xs font-bold ${m.score >= 85 ? 'text-emerald-700' : m.score >= 70 ? 'text-blue-700' : m.score >= 55 ? 'text-amber-700' : 'text-slate-500'}`}>{m.score}%</span>
                                <button className={`p-0.5 ${propSavedIds.has(m.property_id) ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`} onClick={() => handleSaveMatch(m)}>
                                  {propSavedIds.has(m.property_id) ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
                                </button>
                                <button className="p-0.5 text-slate-300 hover:text-green-600" onClick={() => handlePrepareShare(m)} data-testid={`share-lead-match-${m.property_id}`}>
                                  <Share2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            {/* Inline criteria summary */}
                            <div className="flex flex-wrap gap-1 mt-1">
                              {m.criteria?.filter(c => c.status === 'match').map((c, i) => (
                                <span key={i} className="text-[9px] bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded">✓ {c.name}</span>
                              ))}
                              {m.criteria?.filter(c => c.status === 'mismatch').map((c, i) => (
                                <span key={i} className="text-[9px] bg-red-50 text-red-500 px-1 py-0.5 rounded">✗ {c.name}</span>
                              ))}
                              <button className="text-[9px] text-purple-600 hover:underline flex items-center gap-0.5 ml-1" onClick={() => handleExplainMatch(m)} disabled={explainingId === m.property_id} data-testid={`explain-match-${m.property_id}`}>
                                {explainingId === m.property_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                                Explain
                              </button>
                            </div>
                            {matchExplanations[m.property_id] && (
                              <p className="text-[10px] text-slate-600 mt-1.5 bg-purple-50 rounded px-2 py-1 border border-purple-100 italic">{matchExplanations[m.property_id]}</p>
                            )}
                          </div>
                        ))}
                        {propMatches.length > 8 && (
                          <a href="/property-matching" className="text-[10px] text-blue-600 hover:underline block text-center">View all in Property Matching</a>
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Quick Actions */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" className="justify-start" onClick={() => handleInlineLogCall(selectedLead)}>
                        <PhoneCall className="w-4 h-4 mr-2 text-green-600" /> Call
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={() => handleWhatsApp(selectedLead)}>
                        <MessageCircle className="w-4 h-4 mr-2 text-green-500" /> WhatsApp
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={handleLogCall}>
                        <Phone className="w-4 h-4 mr-2 text-blue-600" /> Log Call
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="justify-start">
                            <Calendar className="w-4 h-4 mr-2 text-purple-600" /> Schedule Visit
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Schedule Site Visit</DialogTitle></DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label>Visit Date</Label>
                              <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
                            </div>
                            <Button onClick={handleScheduleVisit} className="w-full">Schedule Visit</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {isAdmin && selectedLead.status !== 'converted' && (
                      <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConvertToClient} data-testid="convert-to-client-button">
                        <UserCheck className="w-4 h-4 mr-2" /> Convert to Client
                      </Button>
                    )}
                    <Button className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                      const propName = window.prompt('Property name for this deal:', selectedLead.property_type ? `${selectedLead.property_type} - ${selectedLead.area_preference || ''}` : '');
                      if (!propName) return;
                      try {
                        await api.post(`/leads/${selectedLead.id}/convert-to-deal`, {
                          property_name: propName,
                          property_type: selectedLead.property_type?.toLowerCase().includes('plot') ? 'plot' : 'flat',
                        });
                        toast.success('Deal created! Check the Pipeline.');
                        fetchLeads(); fetchStats();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create deal'); }
                    }} data-testid="convert-to-deal-button">
                      <TrendingUp className="w-4 h-4 mr-2" /> Convert to Deal
                    </Button>
                    {selectedLead.status === 'converted' && (
                      <div className="mt-3 p-2 bg-purple-50 rounded-lg text-center text-sm text-purple-700 font-medium">Already converted to client</div>
                    )}
                  </Card>

                  {/* Category & Status Update */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Update Lead</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={selectedLead.category} onValueChange={(value) => { handleCategoryChange(selectedLead.id, value); setSelectedLead({ ...selectedLead, category: value }); }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="HOT">HOT</SelectItem>
                            <SelectItem value="WARM">WARM</SelectItem>
                            <SelectItem value="COLD">COLD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={selectedLead.status} onValueChange={(value) => handleStatusChange(selectedLead.id, value)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.filter(s => s.value !== 'all').map(st => (
                              <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>

                  {/* Notes */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Notes</h3>
                    <div className="flex gap-2 mb-3">
                      <Textarea placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={2} className="flex-1" />
                      <Button onClick={handleAddNote} disabled={!newNote.trim()}>Add</Button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {leadNotes.length === 0 ? (
                        <p className="text-sm text-slate-500">No notes yet</p>
                      ) : (
                        leadNotes.map(note => (
                          <div key={note.id} className="bg-slate-50 rounded p-2 text-sm">
                            <p>{note.content}</p>
                            <p className="text-xs text-slate-400 mt-1">{note.created_by_name} - {formatDate(note.created_at)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>

                  {/* Documents */}
                  <Card className="p-4">
                    <EntityDocuments entityType="lead" entityId={selectedLead.id} entityName={selectedLead.name} />
                  </Card>

                  {/* WhatsApp Conversations */}
                  <Card className="p-4" data-testid="lead-detail-whatsapp">
                    <button className="w-full flex items-center justify-between" onClick={() => setLeadWaExpanded(!leadWaExpanded)}>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-green-600" /> WhatsApp
                        {leadWaConvos.length > 0 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{leadWaConvos.length}</span>}
                      </h3>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${leadWaExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    {leadWaExpanded && (
                      <div className="mt-3 space-y-3">
                        {leadWaConvos.length === 0 ? (
                          <div className="text-center py-4">
                            <p className="text-sm text-slate-500">No WhatsApp conversations</p>
                            <a href="/whatsapp" className="text-xs text-green-600 hover:underline mt-1 inline-flex items-center gap-1" data-testid="lead-wa-open-module">
                              Start in WhatsApp module <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ) : (
                          <>
                            {/* Recent messages */}
                            <div className="max-h-48 overflow-y-auto space-y-2 bg-slate-50/50 rounded-lg p-2" data-testid="lead-wa-messages">
                              {leadWaMessages.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No messages yet</p>}
                              {leadWaMessages.slice(-6).map(msg => (
                                <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[85%] rounded-xl px-2.5 py-1.5 ${msg.direction === 'outbound' ? 'bg-green-600 text-white' : 'bg-white text-slate-900 shadow-sm'}`}>
                                    <p className="text-xs whitespace-pre-wrap">{msg.body}</p>
                                    <span className={`text-[9px] ${msg.direction === 'outbound' ? 'text-green-200' : 'text-slate-400'}`}>
                                      {msg.source === 'manual' ? 'Logged · ' : ''}{new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Quick log input */}
                            <div className="flex items-center gap-1.5">
                              <select className="text-[10px] border rounded px-1 py-1 bg-white h-8" value={leadWaDirection} onChange={e => setLeadWaDirection(e.target.value)} data-testid="lead-wa-direction">
                                <option value="outbound">Sent</option>
                                <option value="inbound">Recv</option>
                              </select>
                              <Input placeholder="Log message..." className="flex-1 h-8 text-xs bg-slate-50 border-0" value={leadWaInput} onChange={e => setLeadWaInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLeadWaSend()} data-testid="lead-wa-input" />
                              <Button size="sm" className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700" onClick={handleLeadWaSend} disabled={leadWaSending || !leadWaInput.trim()} data-testid="lead-wa-send">
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Manually logged</span>
                              <a href="/whatsapp" className="text-[10px] text-green-600 hover:underline inline-flex items-center gap-1" data-testid="lead-wa-full-view">
                                Full view <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Activity Timeline */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Activity Timeline</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {leadActivities.length === 0 ? (
                        <p className="text-sm text-slate-500">No activity yet</p>
                      ) : (
                        leadActivities.map(activity => (
                          <div key={activity.id} className="flex gap-3 text-sm">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${activity.activity_type === 'not_interested' ? 'bg-red-400' : activity.activity_type === 'reassignment' ? 'bg-purple-400' : activity.activity_type === 'call' ? 'bg-green-400' : 'bg-slate-300'}`}></div>
                            <div>
                              <p className="text-slate-700">{activity.description}</p>
                              <p className="text-xs text-slate-400">{formatDate(activity.created_at)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Not Interested Reason Modal */}
        <Dialog open={showNotInterestedModal} onOpenChange={(open) => { setShowNotInterestedModal(open); if (!open) { setSelectedReason(''); setOtherReason(''); } }}>
          <DialogContent className="max-w-md" data-testid="not-interested-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ThumbsDown className="w-5 h-5 text-red-500" /> Not Interested - Select Reason
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {NOT_INTERESTED_REASONS.map(reason => (
                <label key={reason} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedReason === reason ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="reason" value={reason} checked={selectedReason === reason} onChange={() => setSelectedReason(reason)} className="text-red-600" />
                  <span className="text-sm">{reason}</span>
                </label>
              ))}
              {selectedReason === 'Other' && (
                <Textarea placeholder="Please specify the reason..." value={otherReason} onChange={(e) => setOtherReason(e.target.value)} rows={2} className="mt-2" data-testid="other-reason-input" />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowNotInterestedModal(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleNotInterestedSubmit} disabled={!selectedReason || (selectedReason === 'Other' && !otherReason.trim())} data-testid="submit-not-interested">
                  Submit
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Confirmation Dialog */}
        <Dialog open={showAssignConfirm} onOpenChange={(open) => { if (!open) { setShowAssignConfirm(false); setPendingAssign(null); } }}>
          <DialogContent className="max-w-sm rounded-xl" data-testid="assign-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#B89B72]" /> Assign Lead
              </DialogTitle>
            </DialogHeader>
            {pendingAssign && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-slate-600">
                  Are you sure you want to assign <span className="font-semibold text-slate-900">{pendingAssign.leadName}</span> to <span className="font-semibold text-slate-900">{pendingAssign.agentName}</span>?
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => { setShowAssignConfirm(false); setPendingAssign(null); }}>Cancel</Button>
                  <Button className="bg-slate-900 hover:bg-slate-800" onClick={confirmAssign} data-testid="confirm-assign-btn">
                    Confirm Assignment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Admin Reassign Modal */}
        <Dialog open={showReassignModal} onOpenChange={(open) => { setShowReassignModal(open); if (!open) setReassignAgentId(''); }}>
          <DialogContent className="max-w-md" data-testid="reassign-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-purple-600" /> Reassign Lead
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {(() => {
                const lead = leads.find(l => l.id === reassignLeadId);
                if (!lead) return null;
                return (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                    <p className="font-medium">{lead.name} - {lead.phone}</p>
                    {lead.previous_agent_name && <p className="text-slate-500">Previous: {lead.previous_agent_name}</p>}
                    {lead.rejection_reason && <p className="text-slate-500">Reason: {lead.rejection_reason}</p>}
                    {(lead.attempt_count || 0) > 0 && (
                      <p className={`${lead.attempt_count >= 3 ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                        Attempts: {lead.attempt_count} {lead.attempt_count >= 3 ? '(Multiple attempts - consider marking as Lost)' : ''}
                      </p>
                    )}
                  </div>
                );
              })()}
              <div>
                <Label>Assign to Agent</Label>
                <Select value={reassignAgentId || 'none'} onValueChange={setReassignAgentId}>
                  <SelectTrigger data-testid="reassign-agent-select"><SelectValue placeholder="Select an agent" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select an agent</SelectItem>
                    {assignableFor(leads.find(l => l.id === reassignLeadId)?.business_vertical).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowReassignModal(false)}>Cancel</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleAdminReassign} disabled={!reassignAgentId || reassignAgentId === 'none'} data-testid="submit-reassign">
                  Reassign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Call Outcome Modal */}
        <Dialog open={showCallOutcome} onOpenChange={setShowCallOutcome}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Call Outcome</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 mb-4">How did the call with <span className="font-medium text-slate-800">{callOutcomeLead?.name}</span> go?</p>
            <div className="space-y-2">
              <Button className="w-full justify-start bg-green-600 hover:bg-green-700 text-white" onClick={() => handleCallOutcome('connected')} data-testid="outcome-connected">
                <CheckCircle className="w-4 h-4 mr-2" /> Connected - Spoke with lead
              </Button>
              <Button className="w-full justify-start bg-orange-500 hover:bg-orange-600 text-white" onClick={() => handleCallOutcome('not_picked')} data-testid="outcome-not-picked">
                <PhoneOff className="w-4 h-4 mr-2" /> Not Picked - Schedule follow-up tomorrow
              </Button>
              <Button className="w-full justify-start bg-blue-500 hover:bg-blue-600 text-white" onClick={() => handleCallOutcome('call_later')} data-testid="outcome-call-later">
                <Clock className="w-4 h-4 mr-2" /> Call Later - Remind in 2 hours
              </Button>
              <Button variant="ghost" className="w-full text-slate-500" onClick={() => { setShowCallOutcome(false); setCallOutcomeLead(null); }}>
                Skip
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Post-Action Note Popup */}
        <Dialog open={showPostActionNote} onOpenChange={(open) => { if (!open) { setShowPostActionNote(false); setPostActionLeadId(null); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{postActionType === 'call' ? 'Call Note' : 'Message Note'}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 mb-2">Add a quick note about this {postActionType === 'call' ? 'call' : 'message'} (optional)</p>
            <Textarea id="post-action-note" placeholder="e.g., Discussed 3BHK options, will send brochure..." rows={3} data-testid="post-action-note" />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" onClick={() => { setShowPostActionNote(false); setPostActionLeadId(null); }}>Skip</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => {
                const note = document.getElementById('post-action-note')?.value;
                handlePostActionNote(note);
              }} data-testid="save-post-action-note">Save Note</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Leads;
